import { useState, type FormEvent } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useSession } from '@/hooks/useSession';
import {
  sendMagicLink,
  buildCallbackUrl,
  signInOrSignUpWithPassword,
  signInWithGoogle,
} from '@/lib/auth/session';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Button } from '@/components/ui/Button';

type Mode = 'password' | 'magic';
type Status = 'idle' | 'submitting' | 'sent' | 'error';

const MIN_PASSWORD_LENGTH = 8;

export function Login() {
  useDocumentTitle('Sign in');
  const [searchParams] = useSearchParams();
  const session = useSession();
  const next = searchParams.get('next') || '/';

  const [mode, setMode] = useState<Mode>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // If already signed in, send them where they were trying to go.
  if (session.data) {
    return <Navigate to={next} replace />;
  }

  async function handleGoogleSignIn() {
    setStatus('submitting');
    setErrorMessage(null);
    try {
      // signInWithOAuth navigates the browser to Google. The line below
      // only resolves locally if the call fails before redirect — in
      // which case we surface the error and let the user retry.
      await signInWithGoogle({ redirectTo: buildCallbackUrl(next) });
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Could not start Google sign-in.',
      );
    }
  }

  async function handlePasswordSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage(null);
    try {
      await signInOrSignUpWithPassword({
        email: email.trim(),
        password,
      });
      // useSession will pick up SIGNED_IN and the Navigate above kicks in.
      // No explicit redirect here — keeps a single source of truth.
    } catch (err) {
      setStatus('error');
      const raw =
        err instanceof Error ? err.message : 'Something went wrong.';
      setErrorMessage(humanizeAuthError(raw));
    }
  }

  async function handleMagicSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage(null);
    try {
      await sendMagicLink({
        email: email.trim(),
        redirectTo: buildCallbackUrl(next),
      });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setErrorMessage(
        err instanceof Error ? err.message : 'Something went wrong.',
      );
    }
  }

  // Magic-link "check your inbox" confirmation screen.
  if (status === 'sent') {
    return (
      <section className="container-prose pt-16 sm:pt-24 pb-24 text-center">
        <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50 mb-4">
          Check your inbox.
        </h1>
        <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-8">
          We sent a sign-in link to{' '}
          <span className="font-medium text-ink-800 dark:text-ink-100">
            {email}
          </span>
          . Open it on this device to continue.
        </p>
        <p className="text-sm text-ink-500 dark:text-ink-400">
          Wrong address?{' '}
          <button
            type="button"
            onClick={() => {
              setStatus('idle');
              setMode('password');
            }}
            className="underline underline-offset-4 hover:text-accent"
          >
            Try another
          </button>
          .
        </p>
      </section>
    );
  }

  return (
    <section className="container-prose pt-16 sm:pt-24 pb-24">
      <p className="text-xs uppercase tracking-[0.22em] text-accent-600 dark:text-accent-400 mb-4">
        Welcome
      </p>
      <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50 mb-4">
        Sign in to Alhudah.
      </h1>
      <p className="text-ink-600 dark:text-ink-300 leading-relaxed mb-8">
        {mode === 'password'
          ? 'New here? Just pick a password — we’ll create your account on the spot. Returning? Use the same email and password.'
          : 'We’ll email you a one-time sign-in link. No password needed.'}
      </p>

      {/*
        OAuth — sits above the email forms so it reads as the primary path
        for users who don't want a separate password. Visible in both
        'password' and 'magic' modes; toggling between modes doesn't hide it.
      */}
      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={status === 'submitting'}
        className="
          w-full h-12 px-4 rounded-xl
          flex items-center justify-center gap-3
          bg-white hover:bg-ink-50
          dark:bg-ink-50 dark:hover:bg-white
          border border-ink-200 dark:border-transparent
          text-ink-900 dark:text-ink-900
          text-sm font-medium
          shadow-[0_1px_2px_rgba(20,40,32,0.06)]
          transition-all duration-150
          hover:shadow-[0_2px_8px_rgba(20,40,32,0.10)]
          disabled:opacity-60 disabled:cursor-not-allowed
          focus:outline-none focus:ring-2 focus:ring-accent/40
        "
      >
        <GoogleGlyph />
        {status === 'submitting' ? 'Redirecting to Google…' : 'Continue with Google'}
      </button>

      {/* Divider — "or" between OAuth and email. The text-bg colors match
          the page bg so the rule appears to pass behind the label. */}
      <div
        role="separator"
        aria-orientation="horizontal"
        className="relative my-6"
      >
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-ink-200 dark:border-ink-800" />
        </div>
        <div className="relative flex justify-center">
          <span className="bg-ink-50 dark:bg-night-900 px-3 text-xs uppercase tracking-[0.22em] text-ink-500 dark:text-ink-400">
            or
          </span>
        </div>
      </div>

      {mode === 'password' ? (
        <form
          onSubmit={handlePasswordSubmit}
          noValidate
          className="space-y-4"
        >
          <label className="block">
            <span className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
              Email address
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              autoFocus
              inputMode="email"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={status === 'submitting'}
              className="
                w-full h-12 px-4 rounded-xl
                bg-white dark:bg-ink-900
                border border-ink-200 dark:border-ink-800
                text-ink-900 dark:text-ink-50 placeholder:text-ink-400
                focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40
                disabled:opacity-50
                transition-colors
              "
            />
          </label>

          <label className="block">
            <span className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
              Password
            </span>
            <input
              type="password"
              required
              autoComplete="current-password"
              minLength={MIN_PASSWORD_LENGTH}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={`At least ${MIN_PASSWORD_LENGTH} characters`}
              disabled={status === 'submitting'}
              className="
                w-full h-12 px-4 rounded-xl
                bg-white dark:bg-ink-900
                border border-ink-200 dark:border-ink-800
                text-ink-900 dark:text-ink-50 placeholder:text-ink-400
                focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40
                disabled:opacity-50
                transition-colors
              "
            />
            <span className="mt-2 block text-xs text-ink-500 dark:text-ink-400">
              Minimum {MIN_PASSWORD_LENGTH} characters. We’ll create your
              account if it doesn’t exist yet.
            </span>
          </label>

          {errorMessage && (
            <p
              role="alert"
              className="text-sm text-red-600 dark:text-red-400"
            >
              {errorMessage}
            </p>
          )}

          <Button
            type="submit"
            size="lg"
            disabled={
              status === 'submitting' ||
              !email ||
              password.length < MIN_PASSWORD_LENGTH
            }
            className="w-full"
          >
            {status === 'submitting' ? 'Signing in…' : 'Continue'}
          </Button>

          <p className="pt-2 text-center text-sm text-ink-500 dark:text-ink-400">
            Forgot your password, or prefer no password at all?{' '}
            <button
              type="button"
              onClick={() => {
                setMode('magic');
                setStatus('idle');
                setErrorMessage(null);
                setPassword('');
              }}
              className="underline underline-offset-4 hover:text-accent"
            >
              Email me a link instead
            </button>
            .
          </p>
        </form>
      ) : (
        <form onSubmit={handleMagicSubmit} noValidate className="space-y-4">
          <label className="block">
            <span className="block text-sm font-medium text-ink-700 dark:text-ink-200 mb-2">
              Email address
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              autoFocus
              inputMode="email"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              disabled={status === 'submitting'}
              className="
                w-full h-12 px-4 rounded-xl
                bg-white dark:bg-ink-900
                border border-ink-200 dark:border-ink-800
                text-ink-900 dark:text-ink-50 placeholder:text-ink-400
                focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40
                disabled:opacity-50
                transition-colors
              "
            />
          </label>

          {errorMessage && (
            <p
              role="alert"
              className="text-sm text-red-600 dark:text-red-400"
            >
              {errorMessage}
            </p>
          )}

          <Button
            type="submit"
            size="lg"
            disabled={status === 'submitting' || !email}
            className="w-full"
          >
            {status === 'submitting' ? 'Sending link…' : 'Email me a link'}
          </Button>

          <p className="pt-2 text-center text-sm text-ink-500 dark:text-ink-400">
            <button
              type="button"
              onClick={() => {
                setMode('password');
                setStatus('idle');
                setErrorMessage(null);
              }}
              className="underline underline-offset-4 hover:text-accent"
            >
              Back to password sign-in
            </button>
          </p>
        </form>
      )}

      <p className="mt-10 text-xs text-ink-500 dark:text-ink-400 leading-relaxed">
        By signing in you agree to be the only person using your account.
        We’ll only contact you about sign-in.{' '}
        <Link to="/" className="underline underline-offset-4">
          Continue without signing in
        </Link>
        .
      </p>
    </section>
  );
}

/**
 * Translates Supabase's raw auth errors into something a user can act on.
 * Supabase masks user-not-found as "Invalid login credentials" by design,
 * so we can't tell wrong-password from no-account-yet — the auto-signup
 * fallback in `signInOrSignUpWithPassword` handles the "no account" case
 * silently; if we still surface "Invalid login credentials" here, it
 * genuinely is a wrong password.
 */
function humanizeAuthError(raw: string): string {
  const lower = raw.toLowerCase();
  if (lower.includes('invalid login credentials')) {
    return 'That password doesn’t match the account for this email. Try again, or use “Email me a link instead”.';
  }
  if (lower.includes('password should be at least')) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (lower.includes('email rate limit') || lower.includes('rate limit')) {
    return 'Too many attempts in a short window. Please wait a minute and try again.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Your email isn’t confirmed yet. Use “Email me a link instead” to sign in, or check your inbox for the confirmation email.';
  }
  return raw;
}

/**
 * Google's official "G" mark — 4-color SVG, inlined to avoid pulling in
 * a new icon dependency. The colors are Google's brand values and stay
 * fixed regardless of the surrounding theme (the button itself is white
 * in both light and dark mode, per Google's brand guidance).
 */
function GoogleGlyph() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
        fill="#4285F4"
      />
      <path
        d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
        fill="#34A853"
      />
      <path
        d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"
        fill="#FBBC05"
      />
      <path
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"
        fill="#EA4335"
      />
    </svg>
  );
}
